import { Navigate, Route, Routes } from 'react-router-dom'
import {
  canAuthorMultiMarket,
  isMultilangPocEnabled
} from '../../../constants/featureFlags'
import ErrorPage, { ErrorType } from '../../../controls/ErrorPage'
import { ProjectType } from '../../../data/gql-gen/dashboard/graphql'
import { useProjectId } from '../../../hooks/useProjectId'
import { useGetProjectByProjectId } from '../../../modules/Project/Project.hooks'
import { AudienceMappingPage } from './AudienceMappingPage'
import { AudiencesPage } from './AudiencesPage'
import { ConfirmPage } from './ConfirmPage'
import { FieldworkPage } from './FieldworkPage'
import { MappingPage } from './MappingPage'
import { MarketsPage } from './MarketsPage'
import { MasterSurveyPage } from './MasterSurveyPage'
import { MultiMarketPage } from './MultiMarketPage'
import { ProjectFlowBreadcrumb } from './ProjectFlowBreadcrumb'
import { ProjectPage } from './ProjectPage'
import { ResponseOptionsMappingPage } from './ResponseOptionsMappingPage'
import { ResultsPage } from './ResultsPage'
import { TranslationsPage } from './TranslationsPage'

export const MultiMarketRoutes = () => {
  const projectId = useProjectId()
  const { project } = useGetProjectByProjectId()

  // Clients get the shared results link and nothing else: an authoring route on
  // a part-launched project lets them edit or remove its draft markets.
  // MultiMarketV2 only — every non-tracker type lands here (see ProjectRoutes).
  if (
    project?.projectType === ProjectType.MultiMarketV2 &&
    !canAuthorMultiMarket()
  ) {
    return (
      <Routes>
        <Route
          index
          element={
            <Navigate to={`/project/${projectId}/results`} replace={true} />
          }
        />
        <Route path="results" element={<ResultsPage />} />
        <Route path="results/:surveyId" element={<ResultsPage />} />
        {/* 404, not a redirect: terminal, so it cannot loop with the dashboard
            redirects these pages already do. */}
        <Route
          path="*"
          element={<ErrorPage errorType={ErrorType.NotFound404} />}
        />
      </Routes>
    )
  }

  return (
    <Routes>
      {/* New multi-market configuration screen at /project/:projectId */}
      <Route index element={<ProjectPage />} />

      {/* Audiences step: market tabs + per-market targeting/screening editor.
          This is the only step scoped to an individual market, so it keeps the
          optional :surveyId param. */}
      <Route path="audiences" element={<AudiencesPage />} />
      <Route path="audiences/:surveyId" element={<AudiencesPage />} />

      {/* Survey step: the shared project questionnaire (the template). The
          questionnaire is project-scoped, so the route carries only :projectId. */}
      <Route
        path="survey"
        element={
          <>
            <ProjectFlowBreadcrumb activeStep="Survey" />
            <MasterSurveyPage />
          </>
        }
      />

      {/* Translations step: the translations editor over the shared questionnaire,
          with the language list aggregated across all markets. */}
      {isMultilangPocEnabled() && (
        <Route
          path="translations"
          element={
            <TranslationsPage
              breadcrumb={<ProjectFlowBreadcrumb activeStep="Translations" />}
            />
          }
        />
      )}

      {/* Mapping step: a read-only Question × Market grid showing which
          questions/options are present, reduced or excluded per market (driven
          by the per-market visibility data on the shared questionnaire). */}
      <Route path="mapping" element={<MappingPage />} />

      {/* Confirm step: per-market launch dashboard (table of markets with
          status + launch/pause actions). */}
      {isMultilangPocEnabled() && (
        <Route path="confirm" element={<ConfirmPage />} />
      )}

      {/* Fieldwork step: per-market completion + quota breakdown. */}
      <Route path="fieldwork" element={<FieldworkPage />} />

      {/* Results step: Combined (project-scoped, no surveyId) vs Per Market
          (the market's :surveyId — individual-market results require it). Both
          render ResultsPage, which toggles between the two views. */}
      <Route path="results" element={<ResultsPage />} />
      <Route path="results/:surveyId" element={<ResultsPage />} />

      {/* Previous multi-market pages, kept until each is superseded by the
          new flow. They keep the old MultiMarketPage layout (sub-nav). The
          legacy audience-mapping screen lives under a distinct path so the
          project-scoped Mapping step above can own /mapping. */}
      <Route element={<MultiMarketPage />}>
        <Route path="markets">
          <Route index element={<MarketsPage />} />
          <Route path=":marketId/audience" element={<h1>Market audience</h1>} />
          <Route path=":marketId/results" element={<h1>Market results</h1>} />
        </Route>
        <Route path="audience-mapping">
          <Route index element={<AudienceMappingPage />} />
          <Route
            path="question/:questionId"
            element={<ResponseOptionsMappingPage />}
          />
        </Route>
      </Route>

      <Route
        path="*"
        element={<ErrorPage errorType={ErrorType.NotFound404} />}
      />
    </Routes>
  )
}
